import type { AircraftCategory } from './types';

/**
 * Bringing an aircraft record written by an older build up to date.
 *
 * Two paths reach pre-v3 rows and only one of them is a migration. The
 * migration converts what is already in the database; a `.fdv` backup restored
 * from a file written months ago arrives *after* the upgrade has run and is
 * written straight into the table, where nothing would ever convert it. Both
 * call this, so a restored aeroplane and a migrated one cannot disagree.
 *
 * **Migration v3 depends on this function's behaviour.** A released migration
 * is frozen (`migrations.ts`, rule 1), and that freeze reaches in here: change
 * what this does to a `class` and you have changed what v3 did on every
 * database that has already run it. New conversions belong in a new migration
 * and a new function, not in edits to this one.
 */

export const SEAPLANE_NOTE =
  'Recorded as a seaplane before this app adopted the CAA register’s categories, which have none.';

/**
 * Rewrite `class` into `category` in place, total and replay-safe.
 *
 * A row that has already been converted has no `class` left to read and comes
 * out untouched. A row an older build left without one at all still ends up
 * with a category, because every caller downstream requires one.
 *
 * The two old values with no direct translation are handled rather than
 * dropped, for reasons `docs/AIRCRAFT-REGISTER.md` spells out: a
 * multi-engine's engine count is corrected upward, because `engineCount` is
 * now the only thing that keeps its hours in the multi-engine column; and a
 * seaplane's category is written into its notes, because that is the pilot's
 * own information and the register has no category to hold it.
 */
export function upgradeAircraftCategory(record: Record<string, unknown>): void {
  const previous = record.class;
  if (typeof previous !== 'string') {
    record.category ??= 'aeroplane';
    return;
  }
  delete record.class;

  if (previous === 'multi-engine-land') {
    const engines = record.engineCount;
    record.engineCount = typeof engines === 'number' && engines >= 2 ? engines : 2;
    record.category = 'aeroplane';
    return;
  }
  if (previous === 'sea') {
    const notes = typeof record.notes === 'string' ? record.notes.trim() : '';
    record.notes = notes === '' ? SEAPLANE_NOTE : `${notes}\n${SEAPLANE_NOTE}`;
    record.category = 'aeroplane';
    return;
  }

  const carriedOver: readonly AircraftCategory[] = ['helicopter', 'glider', 'microlight'];
  record.category = carriedOver.includes(previous as AircraftCategory) ? previous : 'aeroplane';
}
